import { ROLES, SKYNET_INTERNAL_COMPANY_ID } from 'app/auth/authRoles';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { CompanyType } from 'constants/company';
import { AI_DOCUMENT_INTAKE_CLAIMS } from 'constants/composedClaims';
import { TpoEntityStatus } from 'constants/loanPipelines';
import { logger } from 'utils/logger';
import { checkIsAdmin, getPagePermissions } from 'utils/permissions';

import { getAuth } from './auth.selectors';

export const getMaster = createSelector(getAuth, ({ master }) => master);

export const selectUserData = createSelector(getMaster, ({ data }) => data);

// Coerce an empty-string id (an unresolved broker auth context) to null so the
// isNil guards that gate id-interpolated requests reject it, instead of letting
// '' slip into a `//`-holed request URL. Mirrors utils/domain.js getCompanyId().
export const selectMasterCompanyId = createSelector(
  getMaster,
  ({ companyId }) => companyId || null,
);

export const selectMasterCompany = createSelector(getMaster, ({ company }) => company);

const selectMasterCompanyType = createSelector(selectMasterCompany, ({ type }) => type);

export const selectIsBrokerCompany = createSelector(
  selectMasterCompanyType,
  companyType => companyType === CompanyType.BROKER,
);

export const selectIsSubCompanyUser = createSelector(getMaster, ({ subCompany }) => !!subCompany);

// A genuine internal.tpo sub-company user belongs to a broker sub-company, but their MASTER
// company is the host lender instance (company.type === Lender, not Broker), so
// selectIsBrokerCompany alone is false for them. Broker-facing surfaces (conditions fetch
// routing, menu gating) must treat BOTH a Broker-type company AND a sub-company user as a
// broker session — otherwise a sub-company user falls through to the lender path.
export const selectIsBrokerOrSubCompanyUser = createSelector(
  [selectIsBrokerCompany, selectIsSubCompanyUser],
  (isBrokerCompany, isSubCompanyUser) => isBrokerCompany || isSubCompanyUser,
);

export const selectMasterSubCompany = createSelector(
  getMaster,
  ({ subCompany }) => subCompany ?? null,
);

export const selectMasterBranding = createSelector(
  selectIsSubCompanyUser,
  selectMasterCompany,
  (isSubCompanyUser, company) => {
    if (!isSubCompanyUser) {
      return null;
    }

    const branding = company?.branding ?? {};

    return {
      logoUrl: branding.logoUrl ?? null,
      faviconUrl: branding.faviconUrl ?? null,
      primaryColor: branding.primaryColor ?? null,
    };
  },
);

// Coerce an empty-string sub-company id to null (same reason as selectMasterCompanyId)
// so an unresolved broker context never builds a `/sub-companies//` request URL.
export const selectMasterSubCompanyId = createSelector(
  getMaster,
  ({ subCompany }) => subCompany?.id || null,
);

// The backend strips the pipeline/loan-editor claims itself; this selector covers the AdminPanel
// shell routes that carry no page code for the backend to gate (STP-331) and drives the explanatory
// 403 copy. Expressed as "anything that is not Active is pending" rather than a list of gated
// statuses: since it now decides access, a status added or renamed backend-side (the enum lives in
// Skynet.Accounts, with no test tying it to this file) must fail closed instead of silently
// reopening the shell. An absent status — no TPO signup history, i.e. a manually provisioned
// broker, branch or correspondent — is never gated, matching the backend's own null rule.
const APPROVED_CERTIFICATION_STATUS = TpoEntityStatus.ACTIVE.toLowerCase();

export const selectIsSubCompanyCertificationPending = createSelector(
  selectMasterSubCompany,
  subCompany => {
    const status = subCompany?.certificationStatus;

    return !!status && status.toLowerCase() !== APPROVED_CERTIFICATION_STATUS;
  },
);

export const selectLenderOfRecordIdentity = createSelector(selectMasterCompany, company => {
  const lenderInfo = company?.lenderInfo ?? {};

  return {
    legalName: lenderInfo.name || company?.name || null,
    nmlsId: lenderInfo.nmlsId ?? null,
  };
});

export const getUuid = createSelector(getMaster, ({ uuid }) => uuid);

export const selectIsCurrentUserById = memoizeOne(userId =>
  createSelector(getUuid, uuid => userId === uuid),
);

export const getMasterRole = createSelector(getMaster, ({ role }) => {
  if (_.isArray(role)) {
    return role.reduce((acc, x) => {
      const isAllowedTypeOfRole = _.isString(x);

      if (!isAllowedTypeOfRole) {
        logger.warn('Unexpected role:', x);

        return acc;
      }

      acc.push(x.toLowerCase());

      return acc;
    }, []);
  }

  return role;
});

// getMasterRole lower-cases every role name, so compare against the lower-cased constant.
export const selectIsTitleCompanyUser = createSelector(getMasterRole, role =>
  _.isArray(role) ? role.includes(ROLES.TITLE_COMPANY.toLowerCase()) : false,
);

export const selectImpersonateMode = createSelector(getMaster, ({ data }) => data.impersonateMode);

export const getMasterPermissions = createSelector(getMaster, ({ permissions }) => permissions);

export const getIsAdmin = createSelector(getMasterRole, checkIsAdmin);

export const getMasterPermissionsToPage = memoizeOne(pageName =>
  createSelector([getMasterPermissions], getPagePermissions(pageName)),
);

export const selectIsClaimAllowed = memoizeOne(claim =>
  createSelector([getMasterPermissions], permissions => !!permissions?.includes(claim)),
);

export const selectIsAiDocumentIntakeAllowed = createSelector([getMasterPermissions], permissions =>
  AI_DOCUMENT_INTAKE_CLAIMS.some(claim => !!permissions?.includes(claim)),
);

export const getIsFromInternalCompany = createSelector(
  selectMasterCompanyId,
  masterCompanyId => masterCompanyId === SKYNET_INTERNAL_COMPANY_ID,
);

export const selectIsMasterCompany = memoizeOne(companyId =>
  createSelector(selectMasterCompanyId, masterCompanyId => companyId === masterCompanyId),
);
